import { useState } from 'react';
import { cn } from '../../lib/cn';
import { Button } from '../Button/Button';
import { InvSmilePicker } from './InvSmilePicker';
import {
  INV_LOG_AFTER_HAPPENED,
  INV_LOG_BEFORE_SAY,
  INV_LOG_CONCERNS,
  INV_LOG_EVENTS,
} from '../../invisalign/mockData';
import type { InvLogOption, InvStage } from '../../invisalign/types';

export interface InvLogModalProps {
  open: boolean;
  patientName: string;
  /** 'before' = nobody has asked yet (Worth asking). 'after' = already in the programme. */
  mode: 'before' | 'after';
  /** The prospect's stage, for the sub-line. */
  stage?: InvStage | undefined;
  /** Today's #1 question, pre-picked on the "How did it go?" flow (§3.2). */
  topConcern?: string | undefined;
  onClose: () => void;
  /** Receives a readable sentence, e.g. "Orville Allison · yes — send me information · Crooked teeth · #1 How it looks". */
  onSave: (outcomeSentence: string) => void;
}

const WHO_DECIDES = ['Just him', 'Partner', 'Parent'];
const MONTHS = ['This month', 'Next month', 'In 2–3 months', 'Later this year'];

const STAGE_LABEL: Record<InvStage, string> = {
  'worth-asking': 'Worth asking',
  learning: 'Learning',
  ready: 'Ready',
  'consult-booked': 'Consult booked',
  accepted: 'Accepted',
  'asked-not-now': 'Asked — not now',
  closed: 'Closed',
};

type StepId = 'say' | 'happened' | 'smile' | 'top' | 'coming';

/**
 * Log the conversation, one question per screen (spec 45 §3, board INV14LogSteps,
 * supersedes INV8LogModal). Still taps only. *Record the answer* (Worth asking)
 * runs in up to four steps; a non-Yes first answer jumps straight to Save. *How
 * did it go?* runs in three. Back keeps answers; optional steps Skip; Save sits
 * under a green "What happens next" box; closing mid-way asks to discard.
 */
export function InvLogModal({ open, patientName, mode, stage, topConcern, onClose, onSave }: InvLogModalProps) {
  const options = mode === 'before' ? INV_LOG_BEFORE_SAY : INV_LOG_AFTER_HAPPENED;
  const [step, setStep] = useState(0);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [smile, setSmile] = useState<string[]>([]);
  const [top, setTop] = useState<string | null>(topConcern ?? null);
  const [also, setAlso] = useState<string[]>([]);
  const [event, setEvent] = useState<string | null>(null);
  const [month, setMonth] = useState<string>('');
  const [whoDecides, setWhoDecides] = useState<string | null>(null);

  if (!open) return null;

  // The step list depends on the flow and the first answer (§3.1: a non-Yes jumps
  // straight to Save, so only step 1 shows).
  const stepIds: StepId[] =
    mode === 'before'
      ? outcome === 'yes'
        ? ['say', 'smile', 'top', 'coming']
        : ['say']
      : ['happened', 'top', 'coming'];
  const total = stepIds.length;
  const idx = Math.min(step, total - 1);
  const current = stepIds[idx] ?? 'say';
  const isLast = idx === total - 1;

  const chosen: InvLogOption | undefined = options.find((o) => o.key === outcome);
  const title = mode === 'before' ? 'Record the answer' : 'How did it go?';
  const subline = [patientName, stage ? STAGE_LABEL[stage] : null].filter((x): x is string => Boolean(x)).join(' · ');

  const nextEnabled =
    current === 'say' || current === 'happened'
      ? outcome !== null
      : current === 'smile'
        ? smile.length > 0
        : current === 'top'
          ? top !== null
          : true; // coming is optional

  const close = () => {
    if (outcome !== null && !window.confirm('Discard this?')) return;
    onClose();
  };

  const save = () => {
    const parts = [
      patientName,
      chosen ? chosen.label.replace(/^[A-Z]/, (c) => c.toLowerCase()) : '',
      smile[0],
      top ? `#1 ${top}` : '',
    ].filter((x): x is string => Boolean(x));
    onSave(parts.join(' · '));
  };

  const whatNext =
    mode === 'before' && outcome === 'yes'
      ? `Emails start today.${top ? ` The first after the welcome follows “${top}”.` : ''} They’re in Learning.`
      : chosen?.effect ?? '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(20,22,28,0.4)] px-4 py-8" role="dialog" aria-modal="true" aria-labelledby="inv-log-title">
      <div className="flex max-h-full w-full max-w-[600px] flex-col overflow-hidden rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[var(--shadow-modal)]">
        {/* header with progress */}
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-5 py-4">
          <div className="min-w-0">
            <h2 id="inv-log-title" className="m-0 text-[16px] font-bold text-[var(--text-primary)]">{title}</h2>
            <p className="m-0 mt-0.5 text-[12.5px] text-[var(--text-secondary)]">{subline}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              {stepIds.map((s, i) => (
                <span key={s} className={cn('h-1.5 w-1.5 rounded-full', i === idx ? 'bg-[var(--brand-indigo)]' : i < idx ? 'bg-[var(--brand-indigo-tint)]' : 'bg-[var(--border-default)]')} />
              ))}
            </span>
            <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{idx + 1} of {total}</span>
            <button type="button" aria-label="Close" className="text-[20px] leading-none text-[var(--text-muted)] hover:text-[var(--text-primary)]" onClick={close}>×</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* STEP: what did he say / what happened */}
          {current === 'say' || current === 'happened' ? (
            <>
              <StepQ>{mode === 'before' ? 'What did he say?' : 'What happened?'}</StepQ>
              <div className="mt-2.5 flex flex-col gap-1.5">
                {options.map((o) => (
                  <button key={o.key} type="button" onClick={() => { setOutcome(o.key); }}
                    className={cn('flex items-baseline justify-between gap-3 rounded-[12px] border px-3.5 py-3 text-left',
                      o.key === outcome ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]' : 'border-[var(--border-default)] hover:bg-[var(--surface-sunken)]')}>
                    <span className={cn('text-[13.5px] font-semibold', o.red ? 'text-[var(--alert-red)]' : 'text-[var(--text-primary)]')}>{o.label}</span>
                    <span className="text-[11.5px] text-[var(--text-muted)]">{o.effect}</span>
                  </button>
                ))}
              </div>
            </>
          ) : null}

          {/* STEP: smile picker */}
          {current === 'smile' ? (
            <>
              <StepQ>Which looks most like his smile?</StepQ>
              <div className="mt-3"><InvSmilePicker value={smile} onChange={setSmile} /></div>
            </>
          ) : null}

          {/* STEP: #1 question */}
          {current === 'top' ? (
            <>
              <StepQ>What’s his #1 question?</StepQ>
              <div className="mt-2.5 grid grid-cols-3 gap-1.5 max-[520px]:grid-cols-2">
                {INV_LOG_CONCERNS.map((c) => (
                  <button key={c} type="button" onClick={() => { setTop(c); setAlso(also.filter((x) => x !== c)); }}
                    className={cn('rounded-[10px] border px-2.5 py-2 text-left text-[12px] font-semibold',
                      c === top ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]')}>
                    {c}
                  </button>
                ))}
              </div>
              <div className="mt-3 text-[12px] text-[var(--text-muted)]">Did he ask about anything else? <span className="italic">(optional)</span></div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {INV_LOG_CONCERNS.filter((c) => c !== top).map((c) => (
                  <Chip key={c} label={c} on={also.includes(c)} onClick={() => { setAlso(also.includes(c) ? also.filter((x) => x !== c) : [...also, c]); }} />
                ))}
              </div>
              <button type="button" onClick={() => { setTop(null); setAlso([]); setStep(idx + 1); }} className="mt-3 text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">He didn’t ask anything</button>
            </>
          ) : null}

          {/* STEP: anything coming up (optional) */}
          {current === 'coming' ? (
            <>
              <StepQ>Anything coming up? <span className="text-[12px] font-normal italic text-[var(--text-muted)]">(optional)</span></StepQ>
              <div className="mt-3 text-[12px] font-semibold text-[var(--text-muted)]">Who else decides</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {WHO_DECIDES.map((w) => <Chip key={w} label={w} on={whoDecides === w} onClick={() => { setWhoDecides(whoDecides === w ? null : w); }} />)}
              </div>
              <div className="mt-3 text-[12px] font-semibold text-[var(--text-muted)]">An event</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {INV_LOG_EVENTS.map((e) => <Chip key={e} label={e} on={event === e} onClick={() => { setEvent(event === e ? null : e); }} />)}
                {event !== null ? (
                  <select value={month} onChange={(e) => { setMonth(e.target.value); }} aria-label="When"
                    className="rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-card)] px-2 py-1 text-[12px] text-[var(--text-secondary)] outline-none focus:border-[var(--brand-indigo)]">
                    <option value="">When?</option>
                    {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                ) : null}
              </div>
            </>
          ) : null}

          {/* what happens next — on the last step */}
          {isLast ? (
            <div className="mt-5 rounded-[10px] border border-[color-mix(in_srgb,var(--money-green)_30%,transparent)] bg-[#EEF6F2] px-3.5 py-2.5 text-[12.5px] text-[#1B5F45]">
              <span className="font-semibold">What happens next · </span>{whatNext}
            </div>
          ) : null}
        </div>

        {/* footer */}
        <div className="flex items-center justify-between gap-2 border-t border-[var(--border-default)] px-5 py-3">
          <div>
            {idx > 0 ? <Button variant="ghost" size="sm" onClick={() => { setStep(idx - 1); }}>Back</Button> : null}
          </div>
          <div className="flex gap-2">
            {current === 'coming' && !isLast ? <Button variant="ghost" size="sm" onClick={() => { setStep(idx + 1); }}>Skip</Button> : null}
            {isLast ? (
              <Button variant="primary" size="sm" onClick={save}>Save</Button>
            ) : (
              <Button variant="primary" size="sm" disabled={!nextEnabled} onClick={() => { setStep(idx + 1); }}>Next</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepQ({ children }: { children: React.ReactNode }) {
  return <div className="text-[15px] font-bold text-[var(--text-primary)]">{children}</div>;
}
function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={cn('rounded-full border px-3 py-1 text-[12px]',
        on ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)] font-semibold text-[var(--brand-indigo)]' : 'border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]')}>
      {label}
    </button>
  );
}
